import { useCallback, useState } from "react";
import { apiClient } from "../../../api/httpClient";

/**
 * Hook used by public and invite registration screens to detect at email-blur
 * time whether the supplied address already maps to another Vermost portal account.
 *
 * For invite registration pages, pass `inviteToken` from the URL so the API can
 * ignore the placeholder portal user created when the invite was sent.
 *
 * Returns:
 *   { existingAccounts, hasDuplicate, checking, checkEmail, reset }
 */
const useDuplicateEmailCheck = (inviteToken) => {
  const [existingAccounts, setExistingAccounts] = useState([]);
  const [checking, setChecking] = useState(false);

  const reset = useCallback(() => {
    setExistingAccounts([]);
  }, []);

  const checkEmail = useCallback(
    async (email) => {
      if (!email?.includes("@")) {
        setExistingAccounts([]);
        return [];
      }
      setChecking(true);
      try {
        const body = { email: email.trim() };
        if (inviteToken) {
          body.invite_token = inviteToken;
        }
        const data = await apiClient(
          "/accounts-by-email",
          "POST",
          body,
          { include_accounts: true }
        );
        const accounts = Array.isArray(data?.accounts) ? data.accounts : [];
        setExistingAccounts(accounts);
        return accounts;
      } catch (err) {
        // Silently swallow — the blocker is a UX nicety, not a security gate.
        console.warn("accounts-by-email check failed", err);
        setExistingAccounts([]);
        return [];
      } finally {
        setChecking(false);
      }
    },
    [inviteToken]
  );

  return {
    existingAccounts,
    hasDuplicate: existingAccounts.length > 0,
    checking,
    checkEmail,
    reset,
  };
};

export default useDuplicateEmailCheck;
